import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';

import { useIsUserPermittedTo } from '@occc/utils-access-control';
import { Feature } from '@occc/utils-access-control';
import { useUpdateMessagingCampaign } from '@occc-app/dataAccessLayer/messagingCenter/mutations';
import { AudienceFragment, MessagingCampaignDetailsFragment } from '@occc/graphql';
import { snackbar } from '@occc-app/design-system';

import { routeCreator } from '../../../messaging-center.utils';
import {
  MessagingCampaignTabs,
  UPDATED_SUCCESS_MESSAGE,
} from '../messaging-campaign-configure.constants';
import { TabItem } from '../messaging-campaign-configure.types';
import { convertMessagingCampaignToInitialValues } from '../utils/convert-messaging-campaign-to-initial-values.utils';
import { convertFormFieldsToUpdateInput } from '../utils/convert-form-fields-to-update-input.utils';
import {
  MessagingCampaignConfigureFormProps,
  validateConfigureSync,
} from '../components/configure-form/configure-form.types';
import {
  CONFIGURE_PAGE_SUBTITLE,
  GENERAL_ERROR_MESSAGE,
} from '../../../messaging-center.constants';
import { MessagingCampaignTargetFormProps } from '../components/target-form/target-form.types';
import { MessagingCampaignScheduleFormProps } from '../components/schedule-form/schedule-form.types';
import { ConfigureLayoutProps } from '../messaging-campaign-configure.layout';
import { validateTargetSync } from '../components/target-form/target-form.schema';

type GetFormContainerPropsArgs = {
  campaignDetailsRoute: string;
  onCancel: (hasUnsavedChanges?: boolean) => void;
  campaign?: MessagingCampaignDetailsFragment | null;
  audience?: AudienceFragment;
  chainId: string;
  loading: boolean;
};

export const useGetFormContainerProps = ({
  onCancel,
  campaign,
  loading,
  audience,
  chainId,
}: GetFormContainerPropsArgs) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.MESSAGING_CENTER_EDIT);

  const { configure, target, schedule } = useMemo(
    () => convertMessagingCampaignToInitialValues({ campaign, audience }),
    [campaign, audience],
  );

  const isConfigureValid = useMemo(() => validateConfigureSync(configure).success, [configure]);
  const isTargetValid = useMemo(() => validateTargetSync(target).success, [target]);

  const tabValue = useMemo(() => {
    const tabValues = Object.values(MessagingCampaignTabs);
    const values = tabValues.filter((value) => pathname.endsWith(value));

    if (values.length > 0) {
      return values[0];
    }

    return MessagingCampaignTabs.Configure;
  }, [pathname]);

  const setTabValue = useCallback(
    (value: MessagingCampaignTabs) => {
      navigate(value);
    },
    [navigate],
  );

  const onCompleted = useCallback(
    ({ campaign }: { campaign: MessagingCampaignDetailsFragment }) => {
      navigate(routeCreator.campaignDetails(campaign.uuid));
      snackbar.success(UPDATED_SUCCESS_MESSAGE);
    },
    [navigate],
  );

  const onError = (error: ApolloError) => {
    snackbar.error(GENERAL_ERROR_MESSAGE);
    console.error(error);
  };

  const [updateMessagingCampaign, { loading: updateCampaignLoading }] = useUpdateMessagingCampaign({
    onCompleted,
    onError,
  });

  const onNextStepClick = useCallback(() => {
    if (campaign) {
      if (tabValue === MessagingCampaignTabs.Configure) {
        setTabValue(MessagingCampaignTabs.Target);
        return;
      }

      if (tabValue === MessagingCampaignTabs.Target) {
        setTabValue(MessagingCampaignTabs.Schedule);
        return;
      }

      navigate(routeCreator.campaignDetails(campaign.uuid));
    }
  }, [navigate, setTabValue, campaign, tabValue]);

  const title = useMemo(() => {
    if (!isUserPermittedToEdit) {
      return `Messaging Campaign Configurations`;
    }

    return campaign?.title || '';
  }, [campaign?.title, isUserPermittedToEdit]);

  const subtitle = useMemo(
    () => campaign?.description || CONFIGURE_PAGE_SUBTITLE,
    [campaign?.description],
  );

  const tabs: TabItem[] = useMemo(
    () => [
      {
        value: MessagingCampaignTabs.Configure,
        label: 'Configure',
      },
      {
        value: MessagingCampaignTabs.Target,
        label: 'Target',
        disabled: isUserPermittedToEdit && !isConfigureValid,
      },
      {
        value: MessagingCampaignTabs.Schedule,
        label: 'Schedule & Finalize',
        disabled: isUserPermittedToEdit && !isTargetValid,
      },
    ],
    [isUserPermittedToEdit, isConfigureValid, isTargetValid],
  );

  const layoutProps: ConfigureLayoutProps = useMemo(
    () => ({
      title,
      subtitle,
      selectedTab: tabValue,
      onTabSelected: setTabValue,
      loading: updateCampaignLoading || loading,
      tabs,
    }),
    [title, subtitle, tabValue, setTabValue, updateCampaignLoading, loading, tabs],
  );

  const handleConfigureFormSubmit = useCallback<MessagingCampaignConfigureFormProps['onSubmit']>(
    async (values, _form) => {
      const input = convertFormFieldsToUpdateInput({
        campaign,
        configure: values || configure,
        target: target,
        schedule: schedule,
      });

      await updateMessagingCampaign({ variables: { input } });

      setTabValue(MessagingCampaignTabs.Target);
    },
    [campaign, configure, schedule, setTabValue, target, updateMessagingCampaign],
  );

  const configureFormProps: MessagingCampaignConfigureFormProps = useMemo(
    () => ({
      onSubmit: handleConfigureFormSubmit,
      initialValues: configure,
      onCancelClick: onCancel,
      isUserPermittedToEdit: isUserPermittedToEdit,
      loading: loading || updateCampaignLoading,
      onNextStepClick,
    }),
    [
      handleConfigureFormSubmit,
      configure,
      onCancel,
      isUserPermittedToEdit,
      loading,
      updateCampaignLoading,
      onNextStepClick,
    ],
  );

  const handleTargetFormSubmit = useCallback<MessagingCampaignTargetFormProps['onSubmit']>(
    async (values, _form) => {
      const input = convertFormFieldsToUpdateInput({
        campaign,
        configure: configure,
        target: values || target,
        schedule: schedule,
      });

      await updateMessagingCampaign({ variables: { input } });
      setTabValue(MessagingCampaignTabs.Schedule);
    },
    [campaign, configure, schedule, setTabValue, target, updateMessagingCampaign],
  );

  const targetFormProps: MessagingCampaignTargetFormProps = useMemo(
    () => ({
      onSubmit: handleTargetFormSubmit,
      initialValues: target,
      onCancelClick: onCancel,
      chainId: chainId,
      loading: loading || updateCampaignLoading,
      isUserPermittedToEdit: isUserPermittedToEdit,
      onNextStepClick,
    }),
    [
      handleTargetFormSubmit,
      target,
      onCancel,
      chainId,
      loading,
      updateCampaignLoading,
      isUserPermittedToEdit,
      onNextStepClick,
    ],
  );

  const handleScheduleFormSubmit = useCallback<MessagingCampaignScheduleFormProps['onSubmit']>(
    async (values, _form) => {
      const input = convertFormFieldsToUpdateInput({
        campaign,
        configure: configure,
        target: target,
        schedule: values || schedule,
      });

      await updateMessagingCampaign({ variables: { input } });
    },
    [campaign, configure, schedule, target, updateMessagingCampaign],
  );

  const scheduleFormProps: MessagingCampaignScheduleFormProps = useMemo(
    () => ({
      onSubmit: handleScheduleFormSubmit,
      initialValues: schedule,
      onCancelClick: onCancel,
      loading: loading || updateCampaignLoading,
      isUserPermittedToEdit: isUserPermittedToEdit,
      onNextStepClick,
    }),
    [
      handleScheduleFormSubmit,
      schedule,
      onCancel,
      loading,
      updateCampaignLoading,
      isUserPermittedToEdit,
      onNextStepClick,
    ],
  );

  return {
    layoutProps,
    configureFormProps,
    targetFormProps,
    scheduleFormProps,
  };
};
